import React, { useMemo } from 'react';
import { TourStepContent } from '@shared/components/Tour';
import { useTranslation } from '@shared/core';
import type { AccommodationCard } from '../../Accommodations.types';
import { useAccommodationsContext } from '../../context';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { HotelTourStepMap } from './HotelTourStepMap';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { HotelPrice } from '../HotelPrice';

type HotelTourStepContentProps = Readonly<{
  hotel: AccommodationCard;
}>;

export const HotelTourStepContent = React.memo<HotelTourStepContentProps>(({ hotel }) => {
  const { tq } = useTranslation('travelMap');
  const { venueData } = useAccommodationsContext();

  const isMobile = useIsMobileScreen();

  const { price, strikeOutPrice } = useTravelPlacesPricingAndBookingDetailsFragment(hotel.id, 'accommodationPlace', !hotel.id);

  const origin = hotel.lat && hotel.lng ? { lat: hotel.lat, lng: hotel.lng } : undefined;
  const destination = venueData?.latitude && venueData?.longitude ? { lat: venueData.latitude, lng: venueData.longitude } : undefined;

  const contentProps = useMemo(() => {
    const recommendationText = hotel.autoSuggestionRecommendation?.whyThisHotelIsAGreatPick;
    if (recommendationText) {
      return {
        title: isMobile ? tq`Why it’s our pick?` : tq`Why is ${hotel.displayName} our pick?`,
        benefits: [recommendationText],
        isCollapsed: isMobile,
        renderPrice: (
          <HotelPrice
            accommodation={hotel}
            showComparativePrice={false}
          />
        ),
      };
    }
    return { isCollapsed: isMobile };
  }, [hotel, tq, isMobile]);

  return (
    <TourStepContent {...contentProps}>
      <>
        {origin && destination && !isMobile && (
          <Box
            width="100%"
            height={pxToRem(180)}
            flex={1}
            marginBottom={5}
            borderRadius={pxToRem(12)}
          >
            <JoyInteractableMapProvider>
              <HotelTourStepMap
                key={`accommodations-tour-step-map-${hotel.id}`}
                originList={[origin]}
                property={{ price, strikeOutPrice }}
                destination={destination}
              />
            </JoyInteractableMapProvider>
          </Box>
        )}
      </>
    </TourStepContent>
  );
});

HotelTourStepContent.displayName = 'HotelTourStepContent';
